/**
 * Query-key factory y tipos de GENTE de la academia (F3-D) + los pocos query
 * builders Drizzle que SIGUEN siendo local-first.
 *
 * **Plan 06 · E4:** el directorio, el expediente de un alumno, la ficha de un
 * profesor y los vínculos de representante de la academia se sirven por REST.
 * Sus filas dejaron de replicarse (`org_admin_scope`, `campus_admin_scope` y
 * `academy_org` ya no las llevan), así que aquí no queda —ni puede quedar— una
 * consulta local equivalente: sería un camino a datos que el dispositivo no
 * tiene.
 *
 * Lo que sigue local-first, y por qué: **mis** matrículas y **mis**
 * certificados (`my_cohorts_student`, `my_certificates`) y **mi** propio
 * expediente / ficha / tutela (`my_membership_profiles`). Son míos, pequeños y
 * filtrados por `auth.user_id()` — el criterio de `DATA_ACCESS_POLICY.md` §2
 * para quedarse en sync.
 */

import type { components } from "@space/client";
import {
  and,
  certificate,
  cohort,
  cohort_enrollment,
  desc,
  eq,
  guardian_link,
  isNull,
  type PowerSyncDrizzleDB,
  student_profile,
  teacher_profile,
} from "@space/powersync";

/** Claves de caché de la capa de gente (TanStack Query). */
export const academyPeopleQueryKeys = {
  all: ["academy-people"] as const,
  /**
   * Directorio de la academia. **Plan 06 · E4:** una sola clave otra vez — la
   * `directoryRest` que convivía con la local durante la conmutación por flag
   * ya no tiene con qué convivir: la consulta Drizzle se ha borrado y el único
   * origen es `GET /api/academy/:orgId/directory`.
   */
  directory: (orgId: string | undefined) => ["academy-people", "directory", orgId] as const,
  studentProfile: (orgId: string | undefined, userId: string | undefined) =>
    ["academy-people", "student-profile", orgId, userId] as const,
  teacherProfile: (orgId: string | undefined, userId: string | undefined) =>
    ["academy-people", "teacher-profile", orgId, userId] as const,
  /**
   * Identidades de la org. Fuente ÚNICA y única fuente posible: el espejo local
   * de `"user"` desapareció con el recorte de los streams (D3), así que ya no
   * hay «modo» que elegir.
   */
  identities: (orgId: string | undefined) => ["academy-people", "identities", orgId] as const,
  enrollments: (userId: string | undefined) => ["academy-people", "enrollments", userId] as const,
  certificates: (userId: string | undefined) => ["academy-people", "certificates", userId] as const,
  guardianLinks: (orgId: string | undefined) => ["academy-people", "guardians", orgId] as const,
  myStudentProfile: (userId: string | undefined) =>
    ["academy-people", "me", "student", userId] as const,
  myTeacherProfile: (userId: string | undefined) =>
    ["academy-people", "me", "teacher", userId] as const,
  myGuardianLinks: (userId: string | undefined) =>
    ["academy-people", "me", "guardians", userId] as const,
  myRole: (orgId: string | undefined, userId: string | undefined) =>
    ["academy-people", "my-role", orgId, userId] as const,
  orgRow: (orgId: string | undefined) => ["academy-people", "org-row", orgId] as const,
  publicTeacher: (orgSlug: string, slug: string) =>
    ["academy-people", "public-teacher", orgSlug, slug] as const,
};

// ─── Tipos que vienen del servidor (fuente ÚNICA: el OpenAPI de Rust) ────────
//
// E4 · plan 06: estas cuatro formas se DERIVAN del contrato del servidor en vez
// de inferirse del `select()` de Drizzle. No es cosmético — mientras la fila
// venía del espejo local, el tipo lo fijaba el esquema SQLite (booleanos como
// 0/1, `jsonb` como cadena) y había que traducir en cada consumidor. Ahora hay
// un solo tipo por dato y el DTO manda.

/**
 * Fila del directorio SIN identidad: membresía + expediente resumido.
 * La sirve `GET /api/academy/:orgId/directory`, que acota por sede al
 * `campus_admin` — alcance que las reglas de sync no expresan.
 */
export type DirectoryMemberRow = components["schemas"]["OrgDirectoryEntryDTO"];

/** Expediente completo de un alumno (`GET /api/academy/:orgId/students/:userId`). */
export type StudentRecord = components["schemas"]["StudentRecordDTO"];

/** Patch DISPERSO del expediente: lo que no viaja no se toca. */
export type StudentRecordPatch = components["schemas"]["StudentRecordPatchDTO"];

/** Ficha de profesor (`GET /api/academy/:orgId/teachers/:userId`). */
export type TeacherRecord = components["schemas"]["TeacherRecordDTO"];

/** Patch DISPERSO de la ficha de profesor. */
export type TeacherRecordPatch = components["schemas"]["TeacherRecordPatchDTO"];

/** Ficha pública del profesorado de una cohorte (`GET /api/cohorts/:id/teachers`). */
export type CohortTeacherCard = components["schemas"]["CohortTeacherCardDTO"];

/** Vínculo representante↔alumno tal como lo sirve `GET /api/org/listGuardians`. */
export type GuardianLinkRow = components["schemas"]["GuardianLinkDTO"];

/**
 * Identidad de una persona dentro de una organización. **Fuente única** del
 * cliente: la sirve `useOrgIdentities`, nunca un join suelto en cada pantalla.
 *
 * `email === null` no significa «no tiene correo»: significa **redactado** —
 * quien pregunta no tiene `ManageMembers` ni `ViewCampusData` en la org
 * (`org_rbac::can_view_member_emails`).
 */
export interface OrgIdentity {
  userId: string;
  role: string | null;
  campusId: string | null;
  name: string | null;
  email: string | null;
  image: string | null;
}

/** Fila del directorio ya hidratada con identidad (la que ven los selectores). */
export type DirectoryRow = DirectoryMemberRow & Pick<OrgIdentity, "name" | "email" | "image">;

// E4 · BORRADAS: aquí vivían `getOrgDirectoryQuery`, `getStudentProfileQuery`,
// `getTeacherProfileQuery` y `getOrgGuardianLinksQuery` — las cuatro consultas
// Drizzle que leían expedientes, fichas y tutelas de TERCEROS del espejo local.
// Murieron con el recorte de `org_admin_scope` / `campus_admin_scope`: sin esas
// filas replicadas devolverían vacío para todo el mundo, y dejarlas «por si
// acaso» es exactamente el segundo camino que la política prohíbe.
// Las sustituyen `GET /api/academy/:orgId/{directory,students/:id,teachers/:id}`
// y `GET /api/org/listGuardians`, donde el alcance por sede y la capacidad se
// deciden en el ÚNICO sitio con autoridad: el servidor.
//
// (`getOrgIdentitiesQuery` ya se había ido en E2, por la misma razón aplicada a
//  la tabla `"user"`.)

/** Matrículas de un usuario (join con la cohorte) — más recientes primero. */
export function getUserEnrollmentsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select({
      id: cohort_enrollment.id,
      cohortId: cohort_enrollment.cohort_id,
      organizationId: cohort_enrollment.organization_id,
      status: cohort_enrollment.status,
      finalGrade: cohort_enrollment.final_grade,
      finalGradeScheme: cohort_enrollment.final_grade_scheme,
      enrolledAt: cohort_enrollment.enrolled_at,
      completedAt: cohort_enrollment.completed_at,
      cohortName: cohort.name,
      cohortCode: cohort.code,
      courseId: cohort.course_id,
      // Estadísticas de asistencia (columnas añadidas por F3-A). El SELECT las
      // trae si existen; el consumidor las renderiza defensivamente (pueden ser
      // NULL antes de que F3-A aterrice). Se acceden vía `attendanceStatsOf`.
      raw: cohort_enrollment,
    })
    .from(cohort_enrollment)
    .leftJoin(cohort, eq(cohort.id, cohort_enrollment.cohort_id))
    .where(and(eq(cohort_enrollment.user_id, userId), isNull(cohort_enrollment.deleted_at)))
    .orderBy(desc(cohort_enrollment.enrolled_at));
}

/** Certificados de un usuario (local-first; vacío si no están sincronizados). */
export function getUserCertificatesQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(certificate)
    .where(eq(certificate.user_id, userId))
    .orderBy(desc(certificate.issued_at));
}

// ─── auto-vista (alumno / representante) ─────────────────────────────────────

/** MI expediente de alumno (my_membership_profiles). */
export function getMyStudentProfileQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db
    .select()
    .from(student_profile)
    .where(and(eq(student_profile.user_id, userId), isNull(student_profile.deleted_at)));
}

/** MI perfil de profesor (my_membership_profiles). */
export function getMyTeacherProfileQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(teacher_profile).where(eq(teacher_profile.user_id, userId)).limit(1);
}

/** MIS vínculos como representante (soy guardian de N alumnos). */
export function getMyGuardianWardsQuery(db: PowerSyncDrizzleDB, userId: string) {
  return db.select().from(guardian_link).where(eq(guardian_link.guardian_user_id, userId));
}
